import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  HiOutlineEye, 
  HiOutlineEyeSlash, 
  HiOutlineEnvelope,
  HiOutlineLockClosed,
  HiOutlineUser,
  HiOutlineExclamationTriangle
} from 'react-icons/hi2';
import { FaGoogle, FaGithub } from 'react-icons/fa';
import styles from './Auth.module.css';

const Auth = () => {
  const { signIn, signUp, signInWithGoogle, signInWithGithub, resetPassword, error, clearError, isFirebaseEnabled } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    confirmPassword: ''
  });

  // Show Firebase setup message if not configured
  if (!isFirebaseEnabled) {
    return (
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          <div className={styles.authHeader}>
            <div className={styles.logo}>
              <div className={styles.logoIcon}>📋</div>
              <h1 className={styles.logoText}>JobTracker</h1>
            </div>
            <p className={styles.subtitle}>Firebase Setup Required</p>
          </div>

          <div className={styles.setupMessage}>
            <HiOutlineExclamationTriangle className={styles.warningIcon} />
            <h3>Firebase Authentication Not Configured</h3>
            <p>To use authentication features, you need to set up Firebase:</p>
            
            <ol className={styles.setupSteps}>
              <li>Create a Firebase project at <a href="https://console.firebase.google.com" target="_blank" rel="noopener noreferrer">Firebase Console</a></li>
              <li>Enable Authentication with Email/Password, Google, and GitHub</li>
              <li>Copy your Firebase configuration</li>
              <li>Update the <code>.env</code> file in the frontend directory</li>
            </ol>
            
            <p className={styles.setupNote}>
              See <code>FIREBASE_SETUP.md</code> for detailed instructions.
            </p>
            
            <div className={styles.demoNote}>
              <strong>For Demo Purposes:</strong> You can skip authentication by temporarily removing the ProtectedRoute wrapper in App.js
              <br />
              <button 
                onClick={() => window.location.href = '/demo'} 
                className={styles.demoButton}
                style={{ marginTop: 'var(--space-2)' }}
              >
                Continue without Authentication (Demo Mode)
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    clearError();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (showForgotPassword) {
        await resetPassword(formData.email);
        alert('Password reset email sent! Check your inbox.');
        setShowForgotPassword(false);
        setFormData({ email: '', password: '', displayName: '', confirmPassword: '' });
      } else if (isSignUp) {
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        await signUp(formData.email, formData.password, formData.displayName);
      } else {
        await signIn(formData.email, formData.password);
      }
    } catch (error) {
      console.error('Authentication error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Google sign in error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGithubSignIn = async () => {
    setLoading(true);
    try {
      await signInWithGithub();
    } catch (error) {
      console.error('GitHub sign in error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setShowForgotPassword(false);
    setFormData({ email: '', password: '', displayName: '', confirmPassword: '' });
    clearError();
  };

  const toggleForgotPassword = () => {
    setShowForgotPassword(!showForgotPassword);
    setIsSignUp(false);
    setFormData({ email: '', password: '', displayName: '', confirmPassword: '' });
    clearError();
  };

  return (
    <div className={styles.authContainer}>
      <div className={styles.authCard}>
        <div className={styles.authHeader}>
          <div className={styles.logo}>
            <div className={styles.logoIcon}>📋</div>
            <h1 className={styles.logoText}>JobTracker</h1>
          </div>
          <p className={styles.subtitle}>
            {showForgotPassword 
              ? 'Reset your password' 
              : isSignUp 
                ? 'Create your account' 
                : 'Welcome back'
            }
          </p>
        </div>

        {error && (
          <div className={styles.errorAlert}>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.authForm}>
          {isSignUp && !showForgotPassword && (
            <div className="form-group">
              <label htmlFor="displayName" className="form-label">
                Full Name
              </label>
              <div className={styles.inputGroup}>
                <HiOutlineUser className={styles.inputIcon} />
                <input
                  type="text"
                  id="displayName"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter your full name"
                  required
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <div className={styles.inputGroup}>
              <HiOutlineEnvelope className={styles.inputIcon} />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-control"
                placeholder="Enter your email"
                required
              />
            </div>
          </div>

          {!showForgotPassword && (
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className={styles.inputGroup}>
                <HiOutlineLockClosed className={styles.inputIcon} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.passwordToggle}
                >
                  {showPassword ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
                </button>
              </div>
            </div>
          )}

          {isSignUp && !showForgotPassword && (
            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password
              </label>
              <div className={styles.inputGroup}>
                <HiOutlineLockClosed className={styles.inputIcon} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Confirm your password"
                  required
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary btn-lg"
            style={{ width: '100%' }}
          >
            {loading ? 'Please wait...' : (
              showForgotPassword 
                ? 'Send Reset Email' 
                : isSignUp 
                  ? 'Create Account' 
                  : 'Sign In'
            )}
          </button>
        </form>

        {!showForgotPassword && (
          <>
            <div className={styles.divider}>
              <span>or continue with</span>
            </div>

            <div className={styles.oauthButtons}>
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading}
                className={styles.oauthButton}
              >
                <FaGoogle className={styles.oauthIcon} />
                <span>Google</span>
              </button>
              <button
                type="button"
                onClick={handleGithubSignIn}
                disabled={loading}
                className={styles.oauthButton}
              >
                <FaGithub className={styles.oauthIcon} />
                <span>GitHub</span>
              </button>
            </div>
          </>
        )}

        <div className={styles.authFooter}>
          {showForgotPassword ? (
            <p>
              Remember your password?{' '}
              <button
                type="button"
                onClick={toggleForgotPassword}
                className={styles.linkButton}
              >
                Sign In
              </button>
            </p>
          ) : (
            <>
              <p>
                {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button
                  type="button"
                  onClick={toggleMode}
                  className={styles.linkButton}
                >
                  {isSignUp ? 'Sign In' : 'Sign Up'}
                </button>
              </p>
              {!isSignUp && (
                <p>
                  <button
                    type="button"
                    onClick={toggleForgotPassword}
                    className={styles.linkButton}
                  >
                    Forgot your password?
                  </button>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;